package fr.frux.feature.treedetail.site

import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.ConflictReason
import fr.frux.core.common.error.InvalidReason
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.shared.domain.model.SITE_ATTACH_PROPOSAL_METERS
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.allowsContribution
import fr.frux.shared.domain.repository.SiteRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val TAG = "TreeSiteLink"

/** Lieu voisin proposé au rattachement, à [distanceMeters] de l'arbre (calcul client, haversine). */
data class SiteProposal(
    val site: Site,
    val distanceMeters: Double,
)

sealed interface SiteLinkMessage {
    data class Attached(
        val siteName: String,
    ) : SiteLinkMessage

    data object Undone : SiteLinkMessage

    data object Detached : SiteLinkMessage

    data class Failed(
        val error: AppError,
    ) : SiteLinkMessage
}

/**
 * [linkedSiteId] est connu dès le chargement de l'arbre, [linked] seulement une fois le lieu lu :
 * entre les deux (lecture en cours ou échouée), la fiche ne doit pas dire « arbre isolé ».
 */
data class TreeSiteUi(
    val linkedSiteId: Long? = null,
    val linked: Site? = null,
    val proposal: SiteProposal? = null,
    val busy: Boolean = false,
    val message: SiteLinkMessage? = null,
    val canDetach: Boolean = false,
    /** Lieu privé d'autrui sans récolte à venir : la fiche ne propose pas de lancer une récolte. */
    val harvestLocked: Boolean = false,
)

/**
 * Lien de l'arbre à son lieu de cueillette (ADR 0035) : ligne « Lieu » du bloc Identité, carte de
 * proposition pour un arbre libre à moins de [SITE_ATTACH_PROPOSAL_METERS] d'un lieu ouvert, et
 * rattacher, annuler, détacher. Extrait de `TreeDetailViewModel` (ratchet detekt LargeClass).
 *
 * Après une écriture, [TreeSiteUi.linked] vient du lieu rendu par le serveur et le `siteId` de la
 * fiche n'est pas retouché : `SiteRepository.changes` rafraîchit la carte, la fiche lit l'état ici.
 * Un lieu écarté (« Pas maintenant », détaché) n'est plus proposé tant que le ViewModel vit.
 * « Annuler » ramène l'état d'avant le geste, proposition comprise.
 */
class TreeSiteLinkController(
    private val sites: SiteRepository?,
    private val scope: CoroutineScope,
    private val currentUserId: () -> Long?,
) {
    private val _state = MutableStateFlow(TreeSiteUi())
    val state: StateFlow<TreeSiteUi> = _state.asStateFlow()

    private var treeId: Long? = null
    private val dismissed = mutableSetOf<Long>()
    private var loadJob: Job? = null

    /** Proposition acceptée par le dernier rattachement, remise en place par « Annuler ». */
    private var attachedProposal: SiteProposal? = null

    fun onTreeLoaded(tree: Tree) {
        val repository = sites ?: return
        treeId = tree.id
        loadJob?.cancel()
        val siteId = tree.siteId
        // Le nom déjà lu reste affiché au rechargement, un autre lieu (ou aucun) l'efface tout de suite.
        _state.update { it.copy(linkedSiteId = siteId, linked = it.linked?.takeIf { site -> site.id == siteId }) }
        loadJob =
            scope.launch {
                if (siteId != null) {
                    // Toutes les émissions, pas la première : la copie du cache n'a pas `isOwner`, et
                    // « Détacher » d'un lieu privé à moi n'apparaît qu'avec la réponse réseau.
                    repository.getSite(siteId).collect { result ->
                        result
                            .onSuccess { site ->
                                _state.update { it.withLinked(site) }
                            }.onFailure { error ->
                                Logger.d(TAG, "Lieu $siteId illisible : ${error.message}")
                                // Lieu supprimé ou masqué : la copie servie avant ne doit pas rester affichée.
                                if (error is AppError.NotFound || _state.value.linked == null) {
                                    _state.update { it.withoutLinked() }
                                }
                            }
                    }
                } else {
                    val proposal = proposalFor(tree, repository)
                    _state.update { it.withoutLinked().copy(proposal = proposal) }
                }
            }
    }

    fun attach() {
        val repository = sites
        val proposal = _state.value.proposal
        val id = treeId
        if (repository == null || proposal == null || id == null) return
        if (_state.value.busy) return
        // Un rechargement silencieux en vol écraserait le résultat du geste.
        loadJob?.cancel()
        _state.update { it.copy(busy = true) }
        scope.launch {
            repository
                .attachTree(proposal.site.id, id)
                .onSuccess { site ->
                    attachedProposal = proposal
                    _state.update {
                        it.copy(
                            linkedSiteId = site.id,
                            linked = site,
                            proposal = null,
                            busy = false,
                            canDetach = canWrite(site),
                            harvestLocked = lockedFor(site),
                            message = SiteLinkMessage.Attached(site.name),
                        )
                    }
                }.onFailure { failure ->
                    val error = failure.asAppError()
                    Logger.d(TAG, "Rattachement refusé : ${error.message}")
                    // Rattaché ailleurs entre la proposition et le geste, ou lieu devenu privé :
                    // la proposition n'a plus de sens, le reste de la fiche ne bouge pas.
                    val stale = error.isStaleProposal()
                    if (stale) dismissed += proposal.site.id
                    _state.update {
                        it.copy(
                            busy = false,
                            proposal = if (stale) null else it.proposal,
                            message = SiteLinkMessage.Failed(error),
                        )
                    }
                }
        }
    }

    /** « Annuler » de la snackbar de rattachement : détache le lieu qui vient d'être posé. */
    fun undoAttach() = detachLinked(SiteLinkMessage.Undone, restore = attachedProposal)

    fun detach() = detachLinked(SiteLinkMessage.Detached, restore = null)

    fun dismissProposal() {
        val proposal = _state.value.proposal ?: return
        dismissed += proposal.site.id
        _state.update { it.copy(proposal = null) }
    }

    fun consumeMessage() {
        _state.update { it.copy(message = null) }
    }

    /** [restore] non nul : annulation, la proposition revient. Nul : détachement, le lieu est écarté. */
    private fun detachLinked(
        success: SiteLinkMessage,
        restore: SiteProposal?,
    ) {
        val repository = sites
        val site = _state.value.linked
        val id = treeId
        if (repository == null || site == null || id == null) return
        if (_state.value.busy) return
        loadJob?.cancel()
        _state.update { it.copy(busy = true) }
        scope.launch {
            repository
                .detachTree(site.id, id)
                .onSuccess {
                    if (restore == null) dismissed += site.id
                    attachedProposal = null
                    _state.update {
                        it.copy(
                            linkedSiteId = null,
                            linked = null,
                            proposal = restore,
                            canDetach = false,
                            harvestLocked = false,
                            busy = false,
                            message = success,
                        )
                    }
                }.onFailure { failure ->
                    val error = failure.asAppError()
                    Logger.d(TAG, "Détachement refusé : ${error.message}")
                    _state.update { it.copy(busy = false, message = SiteLinkMessage.Failed(error)) }
                }
        }
    }

    private suspend fun proposalFor(
        tree: Tree,
        repository: SiteRepository,
    ): SiteProposal? {
        // Sans compte, aucun geste possible : pas de proposition, la ligne « Lieu » reste lisible.
        if (currentUserId() == null) return null
        val site =
            repository
                .nearestSite(tree.latitude, tree.longitude, SITE_ATTACH_PROPOSAL_METERS)
                .onFailure { Logger.d(TAG, "Lieu voisin indisponible : ${it.message}") }
                .getOrNull()
                ?.takeIf { it.allowsContribution() && it.id !in dismissed }
        return site?.let {
            SiteProposal(
                it,
                Location(tree.latitude, tree.longitude).distanceMetersTo(Location(it.latitude, it.longitude)),
            )
        }
    }

    private fun TreeSiteUi.withLinked(site: Site) =
        copy(linked = site, proposal = null, canDetach = canWrite(site), harvestLocked = lockedFor(site))

    private fun TreeSiteUi.withoutLinked() =
        copy(linked = null, proposal = null, canDetach = false, harvestLocked = false)

    // Un lieu privé reste fermé à un tiers même déverrouillé par une récolte : le serveur lui refuse l'ajout.
    private fun lockedFor(site: Site): Boolean = !site.allowsContribution()

    private fun canWrite(site: Site?): Boolean = site != null && currentUserId() != null && site.allowsContribution()
}

private fun AppError.isStaleProposal(): Boolean =
    (this is AppError.Conflict && reason == ConflictReason.TREE_ALREADY_ATTACHED) ||
        (this is AppError.Invalid && reason == InvalidReason.ATTACH_FORBIDDEN)
